import { youTubeErrorMessage } from '../core/youtube.js';

let apiPromise;
const abortError = () => new DOMException('A newer video was opened.', 'AbortError');

// No third-party request is made until the user explicitly opens a YouTube link.
export function loadYouTubeAPI() {
  if (globalThis.YT?.Player) return Promise.resolve(globalThis.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    const previous = globalThis.onYouTubeIframeAPIReady;
    const script = document.createElement('script');
    const cleanup = () => { clearTimeout(timer); script.onerror = null; if (globalThis.onYouTubeIframeAPIReady === ready) globalThis.onYouTubeIframeAPIReady = previous; };
    const failed = () => { cleanup(); script.remove(); reject(new Error('YouTube could not load. Check your connection and content blockers, then try again.')); };
    const ready = () => {
      cleanup();
      try { previous?.(); } catch { /* Another integration must not prevent this player loading. */ }
      if (globalThis.YT?.Player) resolve(globalThis.YT); else failed();
    };
    const timer = setTimeout(failed, 15000);
    globalThis.onYouTubeIframeAPIReady = ready;
    script.src = 'https://www.youtube.com/iframe_api'; script.async = true; script.onerror = failed;
    document.head.append(script);
  }).catch(error => { apiPromise = null; throw error; });
  return apiPromise;
}

export class YouTubeTransport {
  constructor(player, { onState = () => {}, onError = () => {}, videoId } = {}) {
    this.player = player; this.onState = onState; this.onError = onError;
    this.videoId = videoId ?? player.getVideoData?.()?.video_id;
    this.playerState = player.getPlayerState?.() ?? -1; this.everPlayed = this.playerState === 1;
    this.paused = !this.everPlayed; this.seeking = false; this.destroyed = false;
    this.cuedTime = null; this.desiredRate = Number(player.getPlaybackRate?.()) || 1;
  }
  get availableRates() {
    const rates = this.player.getAvailablePlaybackRates?.() ?? [1];
    const supported = rates.filter(n => Number.isFinite(n) && n >= .25 && n <= 2);
    return supported.length ? supported : [1];
  }
  get state() {
    const p = this.player, rawTime = Number(p.getCurrentTime?.()), rawDuration = Number(p.getDuration?.());
    const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : 0;
    const actualTime = Number.isFinite(rawTime) ? Math.max(0, Math.min(rawTime, duration || Infinity)) : 0;
    // A cued video has no decoded-media clock yet. Its documented startSeconds
    // is the playhead until actual playback starts, including consecutive seeks.
    const time = this.cuedTime ?? actualTime;
    if (this.seeking && !this.cueAwaiting && (Math.abs(time - this.seekTarget) < .5 || Date.now() > this.seekDeadline)) {
      this.seeking = false;
      if (this.playerState === 2 || Date.now() > this.seekDeadline) this.pauseAfterSeek = false;
      queueMicrotask(() => { if (!this.destroyed) this.onState('seeked'); });
    }
    return { time, duration, paused: this.paused, playPending: !!this.pendingPlay,
      rate: this.cuedTime !== null ? this.desiredRate : Number(p.getPlaybackRate?.()) || 1, volume: (Number(p.getVolume?.()) || 0) / 100,
      muted: !!p.isMuted?.(), seeking: this.seeking, availableRates: this.availableRates };
  }
  stateChanged(code) {
    if (this.destroyed) return;
    this.playerState = code;
    const names = { 0: 'ended', 1: 'play', 2: 'pause', 3: 'waiting', 5: 'loadedmetadata', '-1': 'loadedmetadata' };
    if (code === 1) {
      this.everPlayed = true;
      if (this.cuedTime !== null) { this.restoreCueSettings(); this.cuedTime = null; this.cueAwaiting = false; }
      if (this.pauseAfterSeek && !this.pendingPlay) { this.paused = true; this.player.pauseVideo(); this.onState('pause'); return; }
      this.paused = false; this.finishPlay();
    }
    if (code === 5 && this.cuedTime !== null) {
      this.restoreCueSettings(); this.cueAwaiting = false; this.seeking = false;
      this.paused = !this.pendingPlay; this.onState('seeked');
      // cueVideoById is asynchronous. Dispatch the pending play only after the
      // cue is ready, otherwise a later cued event can overwrite playVideo.
      if (this.pendingPlay) { this.pendingPlay.sent = false; this.requestPlay(); }
    }
    if (code === 2 && !this.seeking) this.pauseAfterSeek = false;
    if ([0, 2, 5, -1].includes(code) && !this.pendingPlay) this.paused = true;
    this.onState(names[code] ?? 'timeupdate');
  }
  finishPlay(error) {
    if (!this.pendingPlay) return;
    const pending = this.pendingPlay; this.pendingPlay = null; clearTimeout(pending.timer);
    if (error) pending.reject(error); else pending.resolve();
  }
  requestPlay() {
    if (!this.pendingPlay || this.pendingPlay.sent || this.cueAwaiting) return;
    this.pendingPlay.sent = true;
    try { this.player.playVideo(); } catch (error) { this.paused = true; this.finishPlay(error); }
  }
  play() {
    if (this.destroyed) return Promise.reject(abortError());
    if (this.pendingPlay) return this.pendingPlay.promise;
    if (!this.paused) return Promise.resolve();
    this.paused = false; this.pauseAfterSeek = false;
    let resolve, reject;
    const promise = new Promise((a, b) => { resolve = a; reject = b; });
    this.pendingPlay = { promise, resolve, reject, sent: false, timer: setTimeout(() => {
      this.paused = true;
      this.finishPlay(new Error('Press Play inside the YouTube video to allow playback, then use the practice controls.'));
      this.onState('pause');
    }, 10000) };
    this.requestPlay();
    return promise;
  }
  pause() { if (this.destroyed) return; this.paused = true; this.pauseAfterSeek = this.seeking; this.finishPlay(new DOMException('Playback was paused before it started.', 'AbortError')); this.player.pauseVideo(); this.onState('pause'); }
  seek(time) {
    if (this.destroyed || !Number.isFinite(time)) return;
    const paused = this.paused && !this.pendingPlay, duration = this.state.duration;
    this.seekTarget = Math.max(0, Math.min(time, duration)); this.seekDeadline = Date.now() + 5000; this.seeking = true;
    const actualState = this.player.getPlayerState?.() ?? this.playerState;
    if (paused && (!this.everPlayed || [-1, 0, 5].includes(actualState)) && this.videoId && this.player.cueVideoById) {
      this.cuedMuted = !!this.player.isMuted?.(); this.cuedTime = this.seekTarget; this.cueAwaiting = true; this.pauseAfterSeek = false;
      this.player.cueVideoById({ videoId: this.videoId, startSeconds: this.seekTarget });
    } else {
      this.cuedTime = null; this.pauseAfterSeek = paused && actualState !== 2;
      // YouTube already preserves a genuinely paused state. Sending pauseVideo
      // immediately after seekTo can cancel the queued seek before media loads.
      this.player.seekTo(this.seekTarget, true);
    }
    this.onState('seeking');
  }
  restoreCueSettings() {
    this.player.setPlaybackRate(this.desiredRate);
    if (this.cuedMuted) this.player.mute(); else this.player.unMute();
  }
  rateChanged(rate) {
    if (!this.cueAwaiting && Number.isFinite(rate) && rate >= .25 && rate <= 2) this.desiredRate = rate;
    this.onState('ratechange');
  }
  setRate(rate) {
    const rates = this.availableRates;
    const chosen = rates.reduce((best, value) => Math.abs(value - rate) < Math.abs(best - rate) ? value : best, rates[0]);
    this.desiredRate = chosen; this.player.setPlaybackRate(chosen);
    return chosen;
  }
  setMuted(muted) { if (this.cuedTime !== null) this.cuedMuted = muted; if (muted) this.player.mute(); else this.player.unMute(); this.onState('volumechange'); }
  error(code) {
    const error = new Error(youTubeErrorMessage(code)); this.paused = true;
    this.finishPlay(error); this.onError(error);
  }
  autoplayBlocked() {
    this.paused = true;
    const error = new Error('Press Play inside the YouTube video to allow playback, then use the practice controls.');
    const pending = !!this.pendingPlay; this.finishPlay(error);
    if (!pending) this.onError(error);
    this.onState('pause');
  }
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true; this.finishPlay(abortError());
    try { this.player.destroy(); } catch { /* An already removed iframe is harmless. */ }
  }
}

export async function createYouTubeTransport(container, video, { signal, onState, onError, onReady } = {}) {
  if (signal?.aborted) throw abortError();
  // The shared API request may finish for another player; cancellation only owns this iframe.
  let cancelAPI;
  let API;
  try {
    API = await Promise.race([loadYouTubeAPI(), new Promise((_, reject) => { cancelAPI = () => reject(abortError()); signal?.addEventListener('abort', cancelAPI, { once: true }); })]);
  } finally { signal?.removeEventListener('abort', cancelAPI); }
  if (signal?.aborted) throw abortError();
  const iframe = document.createElement('iframe');
  const url = new URL(`https://www.youtube-nocookie.com/embed/${video.id}`);
  for (const [key, value] of Object.entries({ enablejsapi: 1, playsinline: 1, controls: 1, autoplay: 0, origin: location.origin, start: Math.floor(video.startTime) })) url.searchParams.set(key, value);
  iframe.src = url.href; iframe.title = 'YouTube piano tutorial'; iframe.className = 'tutorial-youtube';
  iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen'; iframe.allowFullscreen = true;
  iframe.referrerPolicy = 'strict-origin-when-cross-origin';
  container.replaceChildren(iframe);
  return new Promise((resolve, reject) => {
    let transport, player, settled = false;
    const cleanup = () => { clearTimeout(timer); signal?.removeEventListener('abort', cancel); };
    const failed = error => { if (settled) { onError?.(error); return; } settled = true; cleanup(); transport?.destroy(); if (!transport) { player?.destroy?.(); iframe.remove(); } reject(error); };
    const cancel = () => failed(abortError());
    const timer = setTimeout(() => failed(new Error('YouTube took too long to initialize. Check your connection or choose another tutorial.')), 20000);
    signal?.addEventListener('abort', cancel, { once: true });
    try {
      player = new API.Player(iframe, { events: {
        onReady: event => {
          if (settled || signal?.aborted) return;
          transport = new YouTubeTransport(event.target, { onState, onError, videoId: video.id });
          settled = true; cleanup(); onReady?.(transport); resolve(transport);
        },
        onStateChange: event => transport?.stateChanged(event.data),
        onPlaybackRateChange: event => transport?.rateChanged(event.data),
        onError: event => { if (transport) transport.error(event.data); else failed(new Error(youTubeErrorMessage(event.data))); },
        onAutoplayBlocked: () => transport?.autoplayBlocked(),
      } });
    } catch (error) { failed(error); }
  });
}
